import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Trash, CaretLeft, CaretRight, Drop, Scales, NotePencil, Smiley, CheckCircle, XCircle, Plus, Minus } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { isBabyPatient } from '@/lib/utils';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

const SKIP_REASONS = [
  { v: 'sem_fome', l: 'Sem fome' },
  { v: 'sem_tempo', l: 'Sem tempo' },
  { v: 'esqueci', l: 'Esqueci' },
  { v: 'outro', l: 'Outro' },
];
const SYMPTOMS = [
  { v: 'nauseas', l: 'Náuseas' },
  { v: 'enfartamento', l: 'Enfartamento' },
  { v: 'refluxo', l: 'Refluxo' },
  { v: 'outro', l: 'Outro' },
];
const TEXTURES = [
  { v: 'liquido', l: 'Líquido' },
  { v: 'pure', l: 'Puré' },
  { v: 'pedacos', l: 'Pedaços' },
];
const BABY_REACTIONS = [
  { v: 'aceitou_bem', l: 'Aceitou bem' },
  { v: 'recusou', l: 'Recusou' },
  { v: 'reacao_alergica', l: 'Reação alérgica' },
];
const MOODS = ['😞', '🙁', '😐', '🙂', '😄'];

// Fase Diário 4: autoavaliação de cumprimento (reaproveita padrão de pills coloridas do humor)
const COMPLIANCE = [
  { v: 'cumpri', l: 'Cumpri', pill: 'bg-brand-green text-white', dot: 'bg-brand-green' },
  { v: 'parcial', l: 'Cumpri parcialmente', pill: 'bg-amber-400 text-white', dot: 'bg-amber-400' },
  { v: 'nao_cumpri', l: 'Não cumpri', pill: 'bg-red-400 text-white', dot: 'bg-red-400' },
];
const COMPLIANCE_CARD = {
  cumpri: 'bg-brand-green/5 border-brand-green/30',
  parcial: 'bg-amber-50 border-amber-200',
  nao_cumpri: 'bg-red-50 border-red-200',
};
const COMPLIANCE_LABEL = { cumpri: 'Cumpri', parcial: 'Cumpri parcialmente', nao_cumpri: 'Não cumpri' };

function Scale({ value, onChange, testid }) {
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} className={`w-9 h-9 rounded-full text-xs font-medium ${value === n ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`${testid}-${n}`}>{n}</button>
      ))}
    </div>
  );
}

// PROMPT 5 — Item 3b: comentário da nutri + resposta inline da paciente
function NutriCommentWithReply({ meal, entry, onReplied }) {
  const [draft, setDraft] = useState(entry.patient_reply || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(entry.patient_reply || ''); }, [entry.patient_reply]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/diary/${entry.id}/reply`, { patient_reply: draft });
      toast.success('Resposta guardada');
      setEditing(false);
      onReplied?.();
    } catch { toast.error('Erro a guardar'); } finally { setSaving(false); }
  };
  return (
    <div className="mx-4 mb-3 space-y-2" data-testid={`meal-${meal.type}-nutri-block`}>
      <div className="bg-brand-green/8 border-l-4 border-brand-green rounded-xl px-3 py-2" data-testid={`meal-${meal.type}-nutri-note`}>
        <span className="text-[10px] text-brand-green font-semibold uppercase tracking-wide">A tua nutricionista escreveu:</span>
        <p className="text-xs text-brand-green mt-0.5">{entry.nutri_comment}</p>
      </div>
      {entry.patient_reply && !editing && (
        <div className="bg-brand-bg/60 border border-brand-border rounded-xl px-3 py-2" data-testid={`meal-${meal.type}-patient-reply`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-brand-muted font-semibold uppercase tracking-wide">A tua resposta</span>
            <button type="button" onClick={() => setEditing(true)} className="text-[10px] text-brand-green hover:underline" data-testid={`meal-${meal.type}-patient-reply-edit`}>Editar</button>
          </div>
          <p className="text-xs text-brand-text mt-0.5 whitespace-pre-line">{entry.patient_reply}</p>
        </div>
      )}
      {(editing || !entry.patient_reply) && (
        <div className="bg-white border border-brand-border rounded-xl px-3 py-2">
          <span className="text-[10px] text-brand-muted font-semibold uppercase tracking-wide">{entry.patient_reply ? 'Editar resposta' : 'Responder'}</span>
          <Textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} maxLength={2000} placeholder="Escreve uma resposta à tua nutricionista..." className="w-full mt-1 text-xs border border-brand-border rounded-lg px-2 py-1.5 resize-none focus:border-brand-green/40" data-testid={`meal-${meal.type}-reply-input`} />
          <div className="flex gap-1.5 mt-1.5">
            <button type="button" onClick={save} disabled={saving || !draft.trim()} className="text-[10px] bg-brand-green text-white rounded-full px-3 py-1 disabled:opacity-50" data-testid={`meal-${meal.type}-reply-save`}>{saving ? 'A guardar…' : 'Responder'}</button>
            {editing && <button type="button" onClick={() => { setEditing(false); setDraft(entry.patient_reply || ''); }} className="text-[10px] text-brand-muted px-2">Cancelar</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function MealCard({ meal, onSave, onDelete, isBaby, onReplied, date }) {
  const confirmDelete = useConfirmDelete();
  const existing = (meal.entries && meal.entries[0]) || null;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => existing ? {
    skipped: !!existing.skipped, skip_reason: existing.skip_reason || '',
    description: existing.description || '', note: existing.note || '',
    hunger_before: existing.hunger_before || null, satiety_after: existing.satiety_after || null,
    symptoms: existing.symptoms || [],
    photos: (existing.photos && existing.photos.length) ? [...existing.photos] : (existing.photo_path ? [existing.photo_path] : []),
    texture: existing.texture || '', baby_reaction: existing.baby_reaction || '',
    meal_time: existing.meal_time || meal.time || '', compliance: existing.compliance || '',
    date: existing.date || date,
  } : { skipped: false, skip_reason: '', description: '', note: '', hunger_before: null, satiety_after: null, symptoms: [], photos: [], texture: '', baby_reaction: '', meal_time: meal.time || '', compliance: '', date });
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // URL of photo being viewed
  const fileRef = useRef();

  const photoUrl = (p) => p ? (p.startsWith('http') ? p : `${API}/api/files/${p}`) : '';

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      // Se já há um registo guardado, podemos usar o endpoint multi-foto
      if (existing?.id) {
        const { data } = await axios.post(`${API}/api/diary/${existing.id}/photos`, fd);
        setForm(f => ({ ...f, photos: data.photos || [] }));
      } else {
        // Sem registo ainda: upload genérico e juntar ao array local; o save mais à frente persiste
        const { data } = await axios.post(`${API}/api/upload`, fd);
        if (data?.path) setForm(f => ({ ...f, photos: [...(f.photos || []), data.path] }));
      }
      toast.success('Foto carregada');
    } catch { toast.error('Erro a carregar foto'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removePhoto = async (idx) => {
    if (existing?.id) {
      const ok = await confirmDelete({ description: 'Vai apagar esta foto do registo da refeição.' });
      if (!ok) return;
      try {
        const { data } = await axios.delete(`${API}/api/diary/${existing.id}/photos/${idx}`);
        setForm(f => ({ ...f, photos: data.photos || [] }));
        toast.success('Foto removida');
      } catch { toast.error('Erro a remover foto'); }
    } else {
      setForm(f => ({ ...f, photos: (f.photos || []).filter((_, i) => i !== idx) }));
    }
  };

  const toggleSymptom = (v) => {
    setForm(f => ({ ...f, symptoms: f.symptoms.includes(v) ? f.symptoms.filter(s => s !== v) : [...f.symptoms, v] }));
  };

  const save = async () => {
    await onSave({
      ...form,
      photo_path: (form.photos && form.photos[0]) || null,
      meal_type: meal.type,
      description: form.description || meal.label,
      plan_linked: true,
      meal_time: form.meal_time || null,
      compliance: form.compliance || null,
      date: form.date || date,
      _existing_id: existing?.id,
    });
    setOpen(false);
  };

  const hasEntry = !!existing;
  const displayTime = (existing && existing.meal_time) || meal.time || '-';
  const cardColor = hasEntry && existing.compliance ? COMPLIANCE_CARD[existing.compliance] : 'bg-white border-brand-border';
  const summaryText = hasEntry && !existing.skipped ? (existing.description && existing.description !== meal.label ? existing.description : (existing.note || '')) : '';
  const summaryPhoto = hasEntry ? ((existing.photos && existing.photos[0]) || existing.photo_path || '') : '';
  const statusColor = hasEntry ? (existing.skipped ? '#9CA3AF' : '#657161') : '#B8BDB6';

  return (
    <div className={`border rounded-2xl overflow-hidden ${cardColor}`} data-testid={`meal-${meal.type}`}>
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: statusColor + '15' }}>
          {hasEntry ? (existing.skipped ? <XCircle size={20} color={statusColor} weight="duotone" /> : <CheckCircle size={20} color={statusColor} weight="duotone" />) : <NotePencil size={18} className="text-brand-muted" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-brand-text">{meal.label}</p>
            {hasEntry && existing.compliance && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full text-white ${COMPLIANCE.find(c => c.v === existing.compliance)?.dot || 'bg-gray-300'}`} data-testid={`meal-${meal.type}-compliance-badge`}>{COMPLIANCE_LABEL[existing.compliance]}</span>
            )}
          </div>
          <p className="text-[10px] text-brand-muted">{displayTime} {hasEntry ? '· registado' : ''}</p>
          {/* Resumo do conteúdo registado (sem abrir) */}
          {hasEntry && (existing.skipped || summaryText || summaryPhoto) && (
            <div className="flex items-center gap-2 mt-1" data-testid={`meal-${meal.type}-summary`}>
              {summaryPhoto && <img src={photoUrl(summaryPhoto)} alt="" className="w-7 h-7 rounded-md object-cover flex-shrink-0" data-testid={`meal-${meal.type}-summary-thumb`} />}
              <p className="text-[11px] text-brand-muted truncate">{existing.skipped ? 'Não comi' : summaryText}</p>
            </div>
          )}
        </div>
        <button onClick={() => setOpen(!open)} className="text-xs px-3 py-1.5 rounded-full bg-brand-green text-white" data-testid={`meal-${meal.type}-toggle`}>{open ? 'Fechar' : (hasEntry ? 'Editar' : 'Registar')}</button>
      </div>

      {/* Planned foods preview (always visible — what to eat/feed for this meal) */}
      {Array.isArray(meal.foods) && meal.foods.length > 0 && (
        <div className="px-4 pb-3 -mt-1" data-testid={`meal-${meal.type}-planned`}>
          <div className="flex flex-wrap gap-1.5">
            {meal.foods.map((f, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-bg border border-brand-border text-brand-muted">{f}</span>
            ))}
          </div>
          {(meal.tip || meal.note) && (
            <p className="text-[10px] text-brand-muted italic mt-1.5">{meal.tip || meal.note}</p>
          )}
        </div>
      )}

      {/* Nutri note + resposta da paciente — PROMPT 5 — Item 3b */}
      {existing?.nutri_comment && (
        <NutriCommentWithReply meal={meal} entry={existing} onReplied={onReplied} />
      )}

      {open && (
        <div className="p-4 border-t border-brand-border space-y-3" data-testid={`meal-${meal.type}-form`}>
          {/* Comi / Não comi */}
          <div className="flex gap-2">
            <button onClick={() => setForm({ ...form, skipped: false })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${!form.skipped ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`meal-${meal.type}-ate`}>{isBaby ? 'Comeu' : 'Comi'}</button>
            <button onClick={() => setForm({ ...form, skipped: true })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${form.skipped ? 'bg-gray-500 text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`meal-${meal.type}-skipped`}>{isBaby ? 'Não comeu' : 'Não comi'}</button>
          </div>

          {/* Hora + data editáveis */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Hora da refeição</Label>
              <Input type="time" value={form.meal_time || ''} onChange={e => setForm({ ...form, meal_time: e.target.value })} className="rounded-xl border-brand-border text-xs h-9" data-testid={`meal-${meal.type}-time-input`} />
            </div>
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Data</Label>
              <Input type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border text-xs h-9" data-testid={`meal-${meal.type}-date-input`} />
            </div>
          </div>

          {/* Autoavaliação de cumprimento (opcional) */}
          {!isBaby && (
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Cumpriste o plano? (opcional)</Label>
              <div className="flex flex-wrap gap-1.5" data-testid={`meal-${meal.type}-compliance`}>
                {COMPLIANCE.map(c => (
                  <button key={c.v} type="button" onClick={() => setForm({ ...form, compliance: form.compliance === c.v ? '' : c.v })} className={`text-[11px] px-2.5 py-1 rounded-full ${form.compliance === c.v ? c.pill : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`meal-${meal.type}-compliance-${c.v}`}>{c.l}</button>
                ))}
              </div>
            </div>
          )}

          {form.skipped ? (
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Motivo</Label>
              <div className="flex flex-wrap gap-1.5">
                {SKIP_REASONS.map(r => (
                  <button key={r.v} type="button" onClick={() => setForm({ ...form, skip_reason: r.v })} className={`text-[11px] px-2.5 py-1 rounded-full ${form.skip_reason === r.v ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`skip-${r.v}`}>{r.l}</button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {!isBaby && (
                <>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Fome antes (1 mín → 5 máx)</Label>
                    <Scale value={form.hunger_before} onChange={v => setForm({ ...form, hunger_before: v })} testid={`hunger-${meal.type}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Saciedade depois (1 mín → 5 máx)</Label>
                    <Scale value={form.satiety_after} onChange={v => setForm({ ...form, satiety_after: v })} testid={`satiety-${meal.type}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Sintomas</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {SYMPTOMS.map(s => (
                        <button key={s.v} type="button" onClick={() => toggleSymptom(s.v)} className={`text-[11px] px-2.5 py-1 rounded-full ${form.symptoms.includes(s.v) ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`symptom-${meal.type}-${s.v}`}>{s.l}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {isBaby && (
                <>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Textura</Label>
                    <div className="flex gap-1.5">
                      {TEXTURES.map(t => (
                        <button key={t.v} type="button" onClick={() => setForm({ ...form, texture: t.v })} className={`text-[11px] px-2.5 py-1 rounded-full ${form.texture === t.v ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`texture-${meal.type}-${t.v}`}>{t.l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Reação</Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {BABY_REACTIONS.map(r => (
                        <button key={r.v} type="button" onClick={() => setForm({ ...form, baby_reaction: r.v })} className={`text-[11px] px-2.5 py-1 rounded-full ${form.baby_reaction === r.v ? 'bg-brand-green text-white' : 'bg-brand-bg border border-brand-border text-brand-muted'}`} data-testid={`baby-reaction-${meal.type}-${r.v}`}>{r.l}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label className="text-[10px] text-brand-muted mb-1 block">Fotos do prato</Label>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid={`photo-input-${meal.type}`} />
                <div className="flex items-center gap-2 flex-wrap" data-testid={`photo-gallery-${meal.type}`}>
                  {(form.photos || []).map((p, idx) => (
                    <div key={idx} className="relative w-14 h-14 group">
                      <button type="button" onClick={() => setLightbox(photoUrl(p))} className="block w-full h-full">
                        <img src={photoUrl(p)} alt={`Foto ${idx + 1}`} className="w-full h-full rounded-lg object-cover" data-testid={`photo-thumb-${meal.type}-${idx}`} />
                      </button>
                      <button type="button" onClick={() => removePhoto(idx)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow opacity-0 group-hover:opacity-100 focus:opacity-100" data-testid={`photo-remove-${meal.type}-${idx}`} aria-label="Remover foto">×</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-dashed border-brand-border text-brand-muted rounded-lg w-14 h-14 flex items-center justify-center disabled:opacity-50 hover:bg-brand-bg/40" data-testid={`photo-btn-${meal.type}`} aria-label="Adicionar foto">
                    {uploading ? <span className="text-[9px]">...</span> : <Camera size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-brand-muted mb-1 block">{isBaby ? 'Notas (opcional)' : 'Notas (opcional)'}</Label>
                <Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="rounded-xl border-brand-border text-xs resize-none" placeholder={isBaby ? 'Como reagiu? Algum detalhe a registar?' : 'Como te sentiste? Algum comentário?'} data-testid={`note-${meal.type}`} />
              </div>
            </>
          )}

          <div className="flex gap-2">
            {hasEntry && (
              <button onClick={async () => { const ok = await confirmDelete({ description: `Vai apagar o registo de ${meal.label}.` }); if (!ok) return; onDelete(existing.id); setOpen(false); }} className="border border-red-300 text-red-500 rounded-full px-3 py-2 text-xs" data-testid={`delete-${meal.type}`}><Trash size={12} className="inline mr-1" />Apagar</button>
            )}
            <button onClick={save} className="flex-1 bg-brand-green text-white rounded-full py-2 text-xs font-medium" data-testid={`save-${meal.type}`}>Guardar</button>
          </div>
        </div>
      )}
      {/* Lightbox para visualização ampliada */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid={`photo-lightbox-${meal.type}`}>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-xl" />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white text-2xl" aria-label="Fechar">×</button>
        </div>
      )}
    </div>
  );
}

export default function DiarioPage() {
  const { user } = useAuth();
  const isBaby = isBabyPatient(user);
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState({ mood: null, water_glasses: 0, weight_kg: '', notes: '' });
  const [waterGoal, setWaterGoal] = useState(8);
  // PROMPT 5 — Item 2: histórico do diário (últimos N dias)
  const [history, setHistory] = useState({ days: [] });
  const [historyDays, setHistoryDays] = useState(7);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLightbox, setHistoryLightbox] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/diary/today-plan?date=${date}`);
      setPlan(data);
      setWaterGoal(data.water_goal || 8);
      const s = data.daily_summary || {};
      setSummary({ mood: s.mood ?? null, water_glasses: s.water_glasses ?? (data.water_today || 0), weight_kg: s.weight_kg != null ? String(s.weight_kg) : '', notes: s.notes || '' });
    } catch { toast.error('Erro a carregar'); }
  }, [date]);
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/api/diary/history?days=${historyDays}`);
      setHistory(data || { days: [] });
    } catch { setHistory({ days: [] }); } finally { setLoadingHistory(false); }
  }, [historyDays]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const saveEntry = async (payload) => {
    try {
      const existingId = payload._existing_id; delete payload._existing_id;
      const body = { ...payload, date: payload.date || date };
      if (existingId) await axios.put(`${API}/api/diary/${existingId}`, body);
      else await axios.post(`${API}/api/diary`, body);
      toast.success('Registado'); fetchData();
    } catch { toast.error('Erro'); }
  };
  const deleteEntry = async (id) => {
    try { await axios.delete(`${API}/api/diary/${id}`); toast.success('Eliminado'); fetchData(); }
    catch { toast.error('Erro'); }
  };

  const saveSummary = async (patch) => {
    const next = { ...summary, ...patch };
    setSummary(next);
    try {
      await axios.post(`${API}/api/diary/daily-summary`, {
        date,
        mood: next.mood,
        water_glasses: next.water_glasses,
        weight_kg: next.weight_kg ? parseFloat(String(next.weight_kg).replace(',', '.')) : null,
        notes: next.notes,
      });
    } catch { toast.error('Erro a guardar resumo'); }
  };

  const navDay = (dir) => {
    const d = new Date(date); d.setDate(d.getDate() + dir);
    setDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="fade-in max-w-2xl mx-auto pb-10" data-testid="diary-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-heading text-3xl text-brand-text">Diário</h1>
        <div className="flex items-center gap-1">
          <button onClick={() => navDay(-1)} className="p-2 rounded-full hover:bg-brand-bg" data-testid="prev-day"><CaretLeft size={16} className="text-brand-green" /></button>
          <span className="text-xs font-medium text-brand-muted px-2">{new Date(date).toLocaleDateString('pt-PT', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
          <button onClick={() => navDay(1)} className="p-2 rounded-full hover:bg-brand-bg" data-testid="next-day"><CaretRight size={16} className="text-brand-green" /></button>
        </div>
      </div>

      {/* Meals from plan */}
      <div className="space-y-2 mb-6">
        {plan?.planned_meals?.length ? plan.planned_meals.map(m => (
          <MealCard key={m.type} meal={m} onSave={saveEntry} onDelete={deleteEntry} isBaby={isBaby} onReplied={() => { fetchData(); fetchHistory(); }} date={date} />
        )) : (
          <div className="bg-white border border-brand-border rounded-2xl p-6 text-center">
            <p className="text-xs text-brand-muted">Ainda não tens plano alimentar ativo. Fala com a tua nutricionista 💚</p>
          </div>
        )}
      </div>

      {/* Daily summary — só faz sentido para adultos. Para bebés, o diário é só refeições. */}
      {!isBaby && (
      <div className="bg-white border border-brand-border rounded-2xl p-4 space-y-4" data-testid="daily-summary">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Resumo do dia</h3>
        {/* Mood */}
        <div>
          <Label className="text-[10px] text-brand-muted mb-1 block flex items-center gap-1"><Smiley size={12} /> Humor geral</Label>
          <div className="flex gap-2">
            {MOODS.map((emoji, i) => (
              <button key={i} onClick={() => saveSummary({ mood: i + 1 })} className={`w-10 h-10 rounded-xl text-xl ${summary.mood === i + 1 ? 'bg-brand-green/10 ring-2 ring-brand-green' : 'bg-brand-bg'}`} data-testid={`mood-${i + 1}`}>{emoji}</button>
            ))}
          </div>
        </div>
        {/* Water */}
        <div>
          <Label className="text-[10px] text-brand-muted mb-1 block flex items-center gap-1"><Drop size={12} /> Água ingerida (objectivo {waterGoal} copos)</Label>
          <div className="flex items-center gap-2">
            <button onClick={() => saveSummary({ water_glasses: Math.max(0, (summary.water_glasses || 0) - 1) })} className="w-9 h-9 rounded-full border border-brand-border text-brand-muted" data-testid="water-minus"><Minus size={14} className="m-auto" /></button>
            <div className="flex-1 bg-brand-bg rounded-full h-2 overflow-hidden">
              <div className="h-full bg-brand-green transition-all" style={{ width: `${Math.min(100, ((summary.water_glasses || 0) / waterGoal) * 100)}%` }} />
            </div>
            <span className="text-sm font-medium text-brand-text w-12 text-right" data-testid="water-count">{summary.water_glasses || 0}/{waterGoal}</span>
            <button onClick={() => saveSummary({ water_glasses: (summary.water_glasses || 0) + 1 })} className="w-9 h-9 rounded-full bg-brand-green text-white" data-testid="water-plus"><Plus size={14} className="m-auto" /></button>
          </div>
        </div>
        {/* Weight */}
        <div>
          <Label className="text-[10px] text-brand-muted mb-1 block flex items-center gap-1"><Scales size={12} /> Peso (opcional)</Label>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.1" min="0" value={summary.weight_kg} onChange={e => setSummary({ ...summary, weight_kg: e.target.value })} onBlur={() => saveSummary({})} placeholder="kg" className="rounded-xl border-brand-border text-xs h-9 w-28" data-testid="weight-input" />
            <span className="text-xs text-brand-muted">kg</span>
          </div>
        </div>
        {/* Notes */}
        <div>
          <Label className="text-[10px] text-brand-muted mb-1 block flex items-center gap-1"><NotePencil size={12} /> Notas do dia</Label>
          <Textarea value={summary.notes} onChange={e => setSummary({ ...summary, notes: e.target.value })} onBlur={() => saveSummary({})} rows={2} placeholder="Como correu o dia?" className="rounded-xl border-brand-border text-xs resize-none" data-testid="notes-input" />
        </div>
      </div>
      )}

      {/* Notas do dia para bebés — mãe/cuidador regista observações gerais do dia */}
      {isBaby && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 space-y-2" data-testid="baby-day-notes">
          <Label className="text-[10px] text-brand-muted mb-1 block flex items-center gap-1"><NotePencil size={12} /> Notas do dia</Label>
          <Textarea value={summary.notes} onChange={e => setSummary({ ...summary, notes: e.target.value })} onBlur={() => saveSummary({})} rows={3} placeholder="Como correu o dia? Apetite, fezes, sono, reações…" className="rounded-xl border-brand-border text-xs resize-none" data-testid="baby-notes-input" />
        </div>
      )}

      {/* PROMPT 5 — Item 2: O meu histórico (últimos N dias) */}
      <div className="mt-6 bg-white border border-brand-border rounded-2xl p-4" data-testid="diary-history">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">O meu histórico</h3>
          <select
            value={historyDays}
            onChange={e => setHistoryDays(parseInt(e.target.value))}
            className="text-[10px] border border-brand-border rounded-full px-2 py-1 bg-white"
            data-testid="history-range-select"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={14}>Últimos 14 dias</option>
            <option value={30}>Últimos 30 dias</option>
          </select>
        </div>
        {loadingHistory ? (
          <p className="text-xs text-brand-muted text-center py-4">A carregar…</p>
        ) : (history.days || []).length === 0 ? (
          <p className="text-xs text-brand-muted text-center py-4" data-testid="history-empty">Sem registos no período seleccionado.</p>
        ) : (
          <div className="space-y-3">
            {history.days.filter(d => d.date !== date).map(day => (
              <div key={day.date} className="border border-brand-border/40 rounded-xl p-3" data-testid={`history-day-${day.date}`}>
                <p className="text-[10px] text-brand-muted mb-2">{new Date(day.date).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <div className="space-y-2">
                  {day.entries.map(e => (
                    <div key={e.id} className="flex gap-2.5 text-xs" data-testid={`history-entry-${e.id}`}>
                      <div className="text-[10px] font-semibold text-brand-green w-16 flex-shrink-0 uppercase">{e.meal_type || '-'}</div>
                      <div className="flex-1 min-w-0">
                        {e.description && <p className="text-brand-text">{e.description}</p>}
                        {e.skipped && <p className="text-brand-muted italic">Não comi · {e.skip_reason || 'sem motivo'}</p>}
                        {(e.photos && e.photos.length > 0) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {e.photos.slice(0, 4).map((p, i) => {
                              const url = p?.startsWith('http') ? p : `${API}/api/files/${p}`;
                              return <button type="button" key={i} onClick={() => setHistoryLightbox(url)} className="w-10 h-10 rounded-md overflow-hidden border border-brand-border" data-testid={`history-thumb-${e.id}-${i}`}><img src={url} alt="" className="w-full h-full object-cover" /></button>;
                            })}
                          </div>
                        )}
                        {(e.hunger_before != null || e.satiety_after != null) && (
                          <p className="text-[10px] text-brand-muted mt-0.5">{e.hunger_before != null && <>Fome: {e.hunger_before}/5 · </>}{e.satiety_after != null && <>Saciedade: {e.satiety_after}/5</>}</p>
                        )}
                        {e.note && <p className="text-[10px] text-brand-text italic mt-0.5">&quot;{e.note}&quot;</p>}
                        {e.nutri_comment && (
                          <div className="mt-1 bg-brand-green/8 border-l-2 border-brand-green rounded px-2 py-1" data-testid={`history-nutri-${e.id}`}>
                            <span className="text-[9px] text-brand-green font-semibold uppercase">Nota da tua nutricionista</span>
                            <p className="text-[11px] text-brand-green">{e.nutri_comment}</p>
                          </div>
                        )}
                        {e.patient_reply && (
                          <div className="mt-1 bg-brand-bg/60 border border-brand-border rounded px-2 py-1" data-testid={`history-reply-${e.id}`}>
                            <span className="text-[9px] text-brand-muted font-semibold uppercase">A minha resposta</span>
                            <p className="text-[11px] text-brand-text">{e.patient_reply}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox global do histórico */}
      {historyLightbox && (
        <div onClick={() => setHistoryLightbox(null)} className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="history-lightbox">
          <img src={historyLightbox} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
